<template>
	<div class="col" :class="{ 'd-none': disabled }">
		<DropdownInput
			:label="content.flight_leaving_from"
			:placeholder="content.flight_station_or_airport"
			:value="getOriginLabel(selectedCode)"
			:dropdownOpen="dropdownOpen"
			@close="closeDropdown"
			@open="openDropdown"
			@search="handleSearch"
			:disabled="disabled"
		>
			<template #dropdown>
				<!-- Mobile view heading + search -->
				<div class="d-md-none d-block">
					<div class="col-12 d-flex flex-row nowrap justify-content-between mb-4">
						<span class="heading text-start text-grey-900">{{ content.flight_leaving_from }}</span>
						<ClsBtn @click="closeDropdown" />
					</div>
					<DropdownInput
						ref="dropdownInputRef"
						:label="content.flight_leaving_from"
						:placeholder="content.flight_station_or_airport"
						:value="getOriginLabel(selectedCode)"
						:dropdownOpen="false"
						@close="closeDropdown"
						@open="openDropdown"
						@search="handleSearch"
					/>
				</div>

				<!-- List -->
				<div class="inner-wrapper bg-white shadow-3 position-relative">
					<div
						class="px-2"
						v-for="(group) in filteredFlightOrigins"
						:key="group.key"
					>
						<!-- Title -->
						<div
							v-if="group.showTitle && group.codes && group.codes.length > 0"
							class="caption-strong text-primary-blue mb-1 mt-1"
						>
							{{ group.type === 'train' ? content.flight_by_train : content.flight_by_plane }}
				</div>

						<!-- Parent Option -->
						<div
							class="mb-2 cursor-pointer option px-3 py-2"
							:class="isSelected(group.key) && 'selected-option'"
							@click="() => handleSelect(group.key)"
							v-if="group.name"
						>
							<div class="d-flex align-items-center gap-2 justify-content-between">
								<div class="d-flex">
									<span class="text-primary me-2">
										<svg width="20" height="20" class="icon text-primary-blue">
											<use
												:xlink:href="group.type === 'train'
													? '#___icon___train'
													: '#___icon___flight'"
											/>
										</svg>
									</span>
									<div>
										<div class="caption-strong text-grey-900">{{ group.name }}</div>
										<div class="subcaption text-grey-700">{{ group.codes.join(",") }}</div>
									</div>
								</div>
								<span class="btn-close-sm ms-2" v-if="isSelected(group.key)">
									<img src="https://www.uk.travelski.com/searchbox/img/svg/close.svg" width="18">
								</span>
							</div>
						</div>

						<!-- Children -->
							
						<template v-if="group.children">
							<div
								v-for="child in group.children"
								:key="child.key"
								class="ms-4 mb-2 cursor-pointer option px-3 py-2"
								:class="isSelected(child.key) && 'selected-option'"
								@click="() => handleSelect(child.key)"
							>
								<div class="d-flex align-items-center gap-2 justify-content-between">
									<div class="d-flex">
										<span class="text-primary me-2">
											<svg width="20" height="20" class="icon text-primary-blue">
												<use xlink:href="#___icon___flight" />
											</svg>
										</span>
										<div>
											<div class="caption-strong text-grey-900">{{ child.name }}</div>
											<div class="subcaption text-grey-700">{{ child.codes.join(",") }}</div>
										</div>
									</div>
									<span class="btn-close-sm ms-2" v-if="isSelected(child.key)">
										<img src="https://www.uk.travelski.com/searchbox/img/svg/close.svg" width="18">
									</span>
								</div>
							</div>
						</template>
					</div>

					<div class="px-3" v-if="!filteredFlightOrigins.length && searchQuery.length >= 3">
						<NoResultsSearch  />
					</div>

					<!-- <p class="no-results">
						{{ content.flight_no_origins }}
					</p> -->

					<div class="form-check mt-4 position-sticky bottom-0 bg-white shadow-3 d-flex gap-2 m-0 ps-5 py-3">
						<input
							class="form-check-input"
							type="checkbox"
							id="noTransport"
							v-model="checked"
						/>
						<label class="form-check-label" for="noTransport">
							{{ content.flight_switch_no }}
						</label>
					</div>
				</div>

				<!-- Confirm Button (only on mobile) -->
				<div class="d-flex justify-content-end mt-3 px-3 d-md-none">
					<button
						class="button-primary primary-normal p-2 mt-2 px-3 font-font-weight-600 font-size-16 cursor-pointer w-100 d-flex align-items-center justify-content-center py-3 py-md-2 px-4"
						@click="handleConfirm"
						:disabled="!selectedCode"
					>
						{{ content.flight_confirm }}
					</button>
				</div>
			</template>
		</DropdownInput>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue"
import { useStore } from "vuex"
import { airportCodeMap } from "@/config/airportCodeMap"
import { trainStationCodeMap } from "@/config/trainStationCodeMap"
import { transportPacks } from "@/config/transportPackagePriorities"
import DropdownInput from "./dropdownInput.vue"
import ClsBtn from '@/vue/common/clsBtn.vue'
import availableTransportPackCodes from "@/vue/planning/computed/availableTransportPackCodes"
import screenType from "@/vue/planning/computed/screenType"
import NoResultsSearch from "./NoResultsSearch.vue"

const store = useStore()

interface FlightOriginGroup {
	name: string
	codes: string[]
	key: string
	type: "train" | "flight"
	showTitle: boolean
	children?: FlightOriginGroup[]
}

const dropdownOpen = ref(false)
const checked = ref(false)
const selectedCode = ref<string>(store.state.flightOrigin)
const searchQuery = ref("")
const filteredFlightOrigins = ref<FlightOriginGroup[]>([])

// Template ref to access the DropdownInput component
const dropdownInputRef = ref<InstanceType<typeof DropdownInput> | null>(null)

const content = computed(() => store.state.content)
const disabled = computed(() => !transportPacks.includes(store.state.packCode))

const groupedTransportOrigins = computed<FlightOriginGroup[]>(() => {
	const allCodes = [...new Set(store.getters.flightOrigins)] as string[]
	const groups: FlightOriginGroup[] = []
	const seenCodes = new Set<string>()
	const titleFlags = { train: false, flight: false }

	

	// Add train stations
	if (availableTransportPackCodes.value.length) {
		const trainGroups = Array.from(trainStationCodeMap.entries()).map(([code, info]) => ({
			name: info.name,
			codes: [code],
			key: code,
			type: "train",
			showTitle: !titleFlags.train && (titleFlags.train = true)
		})) as FlightOriginGroup[]
		groups.push(...trainGroups)
	}

	// Add remaining airports
	for (const code of allCodes) {
		if (seenCodes.has(code)) continue

		const currentCodeInfo = airportCodeMap.get(code);

		const name = currentCodeInfo?.name || "";

		const relatedCodes = allCodes.filter(c => airportCodeMap.get(c)?.parent === code && !seenCodes.has(c))
		relatedCodes.forEach(c => seenCodes.add(c))

		if (!currentCodeInfo?.parent) {
			groups.push({
				name,
				codes: [code],//relatedCodes,
				key: code,//relatedCodes.join("-"),
				type: "flight",
				showTitle: !titleFlags.flight && (titleFlags.flight = true),
				children: relatedCodes.map(c => ({
					name: airportCodeMap.get(c)?.name || "",
					codes: [c],
					key: c,
					type: "flight",
					showTitle: false
				})),
			})
		}
	}

	return groups
})

function getOriginLabel(selectedOrigin: string): string {
	const found = groupedTransportOrigins.value.find(group => group.key === selectedOrigin)
	if (found) return found.name

	const origin = selectedOrigin?.split("-")[0] || ""
	const originLabel = airportCodeMap.get(origin)?.name || trainStationCodeMap.get(origin)?.name || ""
	return originLabel
}

// ✅ FIXED FUNCTION: strict equality match
function isSelected(code: string): boolean {
	return selectedCode.value === code
}

function handleSelect(code: string) {
	if (isSelected(code)) {
		handleUnselect()
		return
	}
	selectedCode.value = code
	if (window.innerWidth >= 1024) {
		store.commit("setFlightOrigin", code)
		//store.dispatch("setDefaultPackageForOriginType", code)
		closeDropdown()
	}
	handleSearch("")
}

function handleUnselect() {
	selectedCode.value = ""
	handleSearch("")
	store.commit("setFlightOrigin", airportCodeMap.get("None")?.name)
}

function handleConfirm() {
	if (selectedCode.value) {
		store.commit("setFlightOrigin", selectedCode.value)
		// store.dispatch("setDefaultPackageForOriginType", selectedCode.value)
		closeDropdown()
	}
}

function handleSearch(query: string) {
	searchQuery.value = query.toLowerCase()
	if (!query) {
		filteredFlightOrigins.value = groupedTransportOrigins.value
		return
	}

	filteredFlightOrigins.value = groupedTransportOrigins.value
		.map(group => {
			const matchGroup = group.name.toLowerCase().includes(searchQuery.value)
			const matchChildren = group.children?.filter(child =>
				child.name.toLowerCase().includes(searchQuery.value) ||
				child.codes.some(code => code.toLowerCase().includes(searchQuery.value))
			)

			if (matchGroup || (matchChildren && matchChildren.length)) {
				return {
					...group,
					children: matchChildren?.length ? matchChildren : group.children
				}
			}
			return null
		})
		.filter(Boolean) as FlightOriginGroup[]
}

function openDropdown() {
	dropdownOpen.value = true
}

function closeDropdown() {
	dropdownOpen.value = false
	selectedCode.value = store.state.flightOrigin || ''
	document.body.classList.remove("overflow-hidden")
}

async function focusInput() {
	await nextTick()
	if (dropdownInputRef.value?.inputRef) {
		dropdownInputRef.value.inputRef.focus()
	}
}

// Watchers
watch(dropdownOpen, async (val) => {
	if (val) {
		filteredFlightOrigins.value = groupedTransportOrigins.value
		// Auto-focus on mobile when dropdown opens
		if (screenType.value === "mobile") {
			await focusInput()
		}
	}
})
watch(checked, val => {
	store.commit("setTransportTrue", !val)
	store.dispatch("selectTransportWithDefaultPackage", !val)
	closeDropdown()
})
watch(() => store.state.transportationSelected, val => {
	checked.value = !val
	
})
watch(() => store.state.flightOrigin, val => {
	selectedCode.value = val
})

</script>


<style scoped>
.inner-wrapper {
	border-radius: var(--border-radius-card);
	max-height: 500px;
	overflow-y: scroll;
	padding: 12px;
}
.option:hover,
.selected-option {
	background-color: var(--blue-100);
}
.heading {
	font-weight: bold;
	font-size: 1.1rem;
}
.cursor-pointer {
	cursor: pointer;
}
.btn-close-sm {
	cursor: pointer;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}
@media(max-width: 1024px) {
	.inner-wrapper {
		box-shadow: none;
		max-height: 425px;
	}
}
</style>
